import { z } from 'zod';

import { AddressSchema, type Address } from './party.js';

/**
 * CMS + Ecommerce — shared schemas for the staff admin (settings) and the
 * public storefront (site info). More land in later phases (pages, navigation,
 * store products, checkout).
 */

/**
 * Safe link targets only. A link MUST be http(s)/mailto/tel or a root-relative
 * path — never javascript:/data:/vbscript: (those would render as an XSS
 * `<a href>` on the public storefront). Shared by nav targets and social links.
 */
export const SAFE_NAV_URL = /^(https?:\/\/|mailto:|tel:|\/)/i;

export const SocialLinksSchema = z
  .object({
    facebook: z.string().max(300).optional(),
    instagram: z.string().max(300).optional(),
    twitter: z.string().max(300).optional(),
    whatsapp: z.string().max(60).optional(),
    website: z.string().max(300).optional(),
    // Mobile-app store/download links, surfaced as "Get the app" buttons in the footer.
    appAndroid: z.string().max(300).optional(),
    appIos: z.string().max(300).optional(),
  })
  .partial()
  .superRefine((v, ctx) => {
    // Social links are rendered into public `<a href>`s — reject unsafe schemes.
    for (const [key, val] of Object.entries(v)) {
      if (val && !SAFE_NAV_URL.test(String(val).trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: 'Link must start with http(s)://, mailto:, tel:, or /',
        });
      }
    }
  });
export type SocialLinks = z.infer<typeof SocialLinksSchema>;

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Selectable storefront fonts (keys; the web maps each to a bundled family). */
export const STORE_FONT_KEYS = ['geist', 'inter', 'poppins', 'cairo'] as const;
export type StoreFontKey = (typeof STORE_FONT_KEYS)[number];
/** Storefront header layout variants. */
export const STORE_HEADER_STYLES = ['standard', 'centered', 'minimal', 'glass'] as const;
export type StoreHeaderStyle = (typeof STORE_HEADER_STYLES)[number];
/** Storefront content locales (the original built-in pair; kept for defaults). */
export const STORE_LOCALES = ['en', 'ar'] as const;
export type StoreLocale = (typeof STORE_LOCALES)[number];
/**
 * Slider display shape. 'rectangle' (default) = the full-width banner carousel
 * (one at a time); 'square' = a square tile shown in a multi-up scrollable row
 * (several per row). Set per slide, so a store can run both at once.
 */
export const SLIDE_SHAPES = ['rectangle', 'square'] as const;
export type SlideShape = (typeof SLIDE_SHAPES)[number];
/** Any enabled storefront language code (catalog-driven, BCP-47-ish). */
export const StoreLocaleCode = z.string().regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/);

/**
 * A delivery city the customer picks at checkout, with its own delivery fee
 * (store currency; '0' = free). Legacy configs stored plain city-name strings —
 * the API parses those back to `{ name, price: '0' }`, so old data keeps working.
 */
export const DeliveryCitySchema = z.object({
  name: z.string().trim().min(1).max(80),
  price: z.string().regex(/^\d+(\.\d{1,4})?$/).default('0'),
});
export type DeliveryCity = z.infer<typeof DeliveryCitySchema>;

/** The four storefront checkout payment methods. Defined here so the CMS
 *  settings schemas below can reference it; the checkout bodies reuse it too. */
export const CheckoutPaymentMethodSchema = z.enum(['cod', 'bank_transfer', 'pay_later', 'fib']);
export type CheckoutPaymentMethod = z.infer<typeof CheckoutPaymentMethodSchema>;

/** Staff-facing settings (read). Includes read-only context (slug, base currency). */
export const CmsSettingsSchema = z.object({
  storeName: z.string(),
  tagline: z.string().nullable(),
  themeColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  fontFamily: z.enum(STORE_FONT_KEYS).nullable(),
  themePreset: z.string().nullable(),
  headerStyle: z.enum(STORE_HEADER_STYLES),
  enabledLocales: z.array(StoreLocaleCode),
  logoAttachmentId: z.string().uuid().nullable(),
  heroImageAttachmentId: z.string().uuid().nullable(),
  contactEmail: z.string().email().nullable(),
  contactPhone: z.string().nullable(),
  address: AddressSchema.nullable(),
  socialLinks: SocialLinksSchema.nullable(),
  currencyCode: z.string().length(3).nullable(),
  /** Delivery cities the customer picks from at checkout (admin-managed), each with its own fee. */
  deliveryCities: z.array(DeliveryCitySchema),
  /** Minimum order subtotal required to check out (store currency); null ⇒ none. */
  minOrderAmount: z.string().nullable(),
  /** WhatsApp number for "Order via WhatsApp" at checkout (raw, human-readable); null ⇒ off. */
  whatsappOrderNumber: z.string().nullable(),
  /** Show the "Buy now" button on product pages (customer-mobile). */
  buyNowEnabled: z.boolean(),
  /** Enabled checkout payment methods (raw stored list; empty ⇒ all four). */
  paymentMethods: z.array(CheckoutPaymentMethodSchema),
  isPublished: z.boolean(),
  /** Read-only context for the admin UI. */
  slug: z.string(),
  baseCurrencyCode: z.string(),
  defaultLocale: z.string(),
});
export type CmsSettings = z.infer<typeof CmsSettingsSchema>;

/** The only fields staff may edit. */
export const CmsSettingsUpdateSchema = z.object({
  storeName: z.string().min(1).max(200).optional(),
  tagline: z.string().max(300).nullable().optional(),
  themeColor: z.string().regex(HEX_COLOR).nullable().optional(),
  secondaryColor: z.string().regex(HEX_COLOR).nullable().optional(),
  fontFamily: z.enum(STORE_FONT_KEYS).nullable().optional(),
  themePreset: z.string().max(32).nullable().optional(),
  headerStyle: z.enum(STORE_HEADER_STYLES).optional(),
  enabledLocales: z.array(StoreLocaleCode).min(1).max(40).optional(),
  logoAttachmentId: z.string().uuid().nullable().optional(),
  heroImageAttachmentId: z.string().uuid().nullable().optional(),
  contactEmail: z.string().email().nullable().optional(),
  contactPhone: z.string().max(32).nullable().optional(),
  address: AddressSchema.nullable().optional(),
  socialLinks: SocialLinksSchema.nullable().optional(),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  // Admin-managed delivery cities + per-city fee (deduped/trimmed server-side);
  // shown as a dropdown at checkout. Empty array ⇒ customers type their city freely.
  deliveryCities: z.array(DeliveryCitySchema).max(300).optional(),
  // Minimum order subtotal (store currency). null/'' ⇒ no minimum.
  minOrderAmount: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  // WhatsApp ordering number (digits / +country code / spaces ok — normalized for wa.me on the storefront). '' ⇒ off.
  whatsappOrderNumber: z.string().max(40).nullable().optional(),
  // Show/hide the "Buy now" button on product pages.
  buyNowEnabled: z.boolean().optional(),
  // Enabled checkout payment methods (subset of the four). Empty array ⇒ treated as "all enabled".
  paymentMethods: z.array(CheckoutPaymentMethodSchema).max(4).optional(),
  isPublished: z.boolean().optional(),
});
export type CmsSettingsUpdate = z.infer<typeof CmsSettingsUpdateSchema>;

// ---- CMS pages ----

export const CmsPageStatusSchema = z.enum(['draft', 'published']);
export type CmsPageStatus = z.infer<typeof CmsPageStatusSchema>;

/** URL slug: lowercase, alphanumeric, hyphen-separated. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const CmsPageSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  bodyHtml: z.string().nullable(),
  status: CmsPageStatusSchema,
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  isHome: z.boolean(),
  sort: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CmsPage = z.infer<typeof CmsPageSchema>;

export const CmsPageInputSchema = z.object({
  slug: z.string().min(1).max(160).regex(SLUG),
  title: z.string().min(1).max(200),
  bodyHtml: z.string().max(200_000).nullable().optional(),
  status: CmsPageStatusSchema.optional(),
  seoTitle: z.string().max(200).nullable().optional(),
  seoDescription: z.string().max(320).nullable().optional(),
  isHome: z.boolean().optional(),
  sort: z.number().int().min(0).max(100000).optional(),
});
export type CmsPageInput = z.infer<typeof CmsPageInputSchema>;

// ---- CMS navigation ----

export const CmsNavLocationSchema = z.enum(['header', 'footer']);
export const CmsNavTargetTypeSchema = z.enum(['page', 'url']);

export const CmsNavItemSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  targetType: z.enum(['page', 'url']),
  target: z.string(),
  location: CmsNavLocationSchema,
  sort: z.number().int(),
});
export type CmsNavItem = z.infer<typeof CmsNavItemSchema>;

export const CmsNavInputSchema = z
  .object({
    label: z.string().min(1).max(120),
    targetType: CmsNavTargetTypeSchema,
    target: z.string().min(1).max(2000),
    location: CmsNavLocationSchema.optional(),
    sort: z.number().int().min(0).max(100000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.targetType === 'url' && !SAFE_NAV_URL.test(v.target.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target'],
        message: 'Link URL must start with http(s)://, mailto:, tel:, or /',
      });
    }
    if (v.targetType === 'page' && !SLUG.test(v.target.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['target'], message: 'Page target must be a page slug' });
    }
  });
export type CmsNavInput = z.infer<typeof CmsNavInputSchema>;

// ---- Public storefront DTOs ----

/** A resolved public nav entry — `href` is ready to use (page → path, url → external). */
export interface StorefrontNavItem {
  id: string;
  label: string;
  href: string;
  external: boolean;
  location: 'header' | 'footer';
}

/** A public homepage banner/slide (no internal ids). */
export interface StorefrontSlide {
  id: string;
  imageUrl: string | null;
  title: string | null;
  link: string | null;
  /** Display shape — 'rectangle' full-width carousel, 'square' multi-up tile row. */
  shape: SlideShape;
}

/** Public storefront site info (no internal ids). */
export interface StorefrontInfo {
  slug: string;
  storeName: string;
  tagline: string | null;
  themeColor: string | null;
  secondaryColor: string | null;
  /** Font KEY (geist|inter|poppins|cairo); the web maps it to a CSS family. */
  fontFamily: StoreFontKey | null;
  headerStyle: StoreHeaderStyle;
  /** Public media URL for the logo, or null. */
  logoUrl: string | null;
  /** Public media URL for the home hero image, or null. */
  heroUrl: string | null;
  currencyCode: string; // resolved: override ?? tenant base
  /**
   * Backend per-currency decimal places (code → exponent, e.g. { IQD: 0, USD: 2 }).
   * Public surfaces (web storefront, customer-mobile) have no /currencies access,
   * so they take precision from here to format money — IQD as whole dinars, etc.
   */
  currencyDecimals: Record<string, number>;
  locale: string; // the active content locale for this response
  /** Content locales this storefront offers (drives the language toggle). */
  enabledLocales: string[];
  contactEmail: string | null;
  contactPhone: string | null;
  address: Address | null;
  socialLinks: SocialLinks | null;
  nav: StorefrontNavItem[];
  /** Homepage banner slides (active, sorted); empty when none configured. */
  slides: StorefrontSlide[];
  /** True when the tenant has FIB online payment configured + enabled. */
  fibEnabled: boolean;
  /** True when the tenant has phone OTP login configured + enabled. */
  otpEnabled: boolean;
  /** True when the `reviews` module is enabled (product reviews shown + accepted). */
  reviewsEnabled: boolean;
  /** True when the `wishlist` module is enabled (save-to-wishlist shown). */
  wishlistEnabled: boolean;
  /** True when the `storefront_ai` module is on AND the tenant has an AI key — shows the AI shopping assistant. */
  aiAssistantEnabled: boolean;
  /**
   * True when the `tax` module is enabled. Gates tax INPUT affordances + the
   * checkout tax note ONLY — tax DISPLAY (an order's Tax line) stays conditional
   * on the stored taxTotal>0 so historical taxed orders still render.
   */
  taxEnabled: boolean;
  /** Delivery city NAMES (admin-managed); empty ⇒ free-text city input. Kept for
   *  older clients — new clients use `deliveryCities` for the per-city fee. */
  cities: string[];
  /** Delivery cities with their per-city fee (store currency; '0' = free). */
  deliveryCities: DeliveryCity[];
  /** Minimum order subtotal (store currency) required to check out; null ⇒ no minimum. */
  minOrderAmount: string | null;
  /** WhatsApp number (digits only, wa.me-ready) for "Order via WhatsApp" at checkout; null ⇒ off. */
  whatsappNumber: string | null;
  /** Show the "Buy now" button on product pages (customer-mobile). Defaults on. */
  buyNowEnabled: boolean;
  /** Enabled checkout payment methods (resolved: 'fib' dropped unless FIB is configured). */
  paymentMethods: CheckoutPaymentMethod[];
  /**
   * Self-selectable customer types shown at registration — the tenant's active
   * price lists ("customer groups", e.g. Stationery / Office). Empty when the
   * `pricing` module is off or no active lists exist (the selector then hides).
   */
  customerTypes: { id: string; name: string }[];
  /**
   * Cashback/loyalty program advertisement. Present when the `cashback` module
   * is on AND its online channel is enabled: logged-in customers can then spend
   * their wallet at checkout (orders in `currencyCode`, the tenant base) and
   * earn `earnRatePct`% on fulfilment. Null/absent ⇒ hide every loyalty affordance.
   */
  loyalty?: StorefrontLoyalty | null;
  /**
   * The one-time welcome credit a NEW customer gets for registering, so the
   * signup screen can say what it is worth instead of asking for a password and
   * offering nothing.
   *
   * Deliberately NOT part of `loyalty`: that object is gated on the online
   * channel (can this shopper spend the wallet at checkout?), while the welcome
   * credit is granted whatever the channels say. Tying them together would hide
   * a gift the shop really does pay.
   *
   * This is the figure for a shopper who picks NO customer type — which is every
   * self-service signup in the app. Null when the module is off, the amount is
   * zero, or the shop does not pay normal shoppers.
   */
  signupBonus?: { amount: string; currencyCode: string } | null;
  /**
   * True when a sale on this surface may NOT drive stock below zero (Settings →
   * Inventory, resolved for THIS client — the customer app can be stricter than
   * the website). Out-of-stock products are then usually hidden outright, but a
   * cached card, a shared link or a wishlist row can still reach the shopper:
   * with this on, those must show as unavailable instead of offering a buy.
   * Absent on an older API ⇒ treat as false (nothing blocked), as before.
   */
  blockOutOfStock?: boolean;
}

/** The storefront's advertised loyalty program (see StorefrontInfo.loyalty). */
export interface StorefrontLoyalty {
  /** Percent of the eligible subtotal earned on fulfilment. */
  earnRatePct: number;
  /** Wallet currency — cashback can only be spent on orders in this currency. */
  currencyCode: string;
}

/** A chat turn sent to / from the storefront AI shopping assistant. */
export const StorefrontAiMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(4000),
});
export type StorefrontAiMessage = z.infer<typeof StorefrontAiMessageSchema>;

/** Body for POST /storefront/:slug/ai/chat — the running conversation. */
export const StorefrontAiChatSchema = z.object({
  messages: z.array(StorefrontAiMessageSchema).min(1).max(20),
});
export type StorefrontAiChatInput = z.infer<typeof StorefrontAiChatSchema>;

/** Reply from the storefront AI: the assistant text + any products it surfaced (rendered as cards). */
export interface StorefrontAiReply {
  reply: string;
  products: StorefrontProductSummary[];
  /** Set when the assistant placed an order this turn (the customer confirmed) — the new order number. */
  orderNumber?: string;
}

/** What the storefront AI is doing between answer rounds (drives the widget's status line). */
export type StorefrontAiActivity = 'searching' | 'product' | 'ordering';

/**
 * SSE events streamed by POST /storefront/:slug/ai/chat/stream. `delta` chunks
 * build the visible answer; `reset` means the streamed text so far was a
 * preliminary round (the model went off to use tools) and should be discarded;
 * `done` carries the authoritative final payload (same shape as the JSON
 * endpoint). `error.code` is machine-readable only — no provider details leak.
 */
export type StorefrontAiStreamEvent =
  | { type: 'status'; status: StorefrontAiActivity }
  | { type: 'delta'; text: string }
  | { type: 'reset' }
  | ({ type: 'done' } & StorefrontAiReply)
  | { type: 'error'; code: string };

/** Staff CMS: a homepage slide/banner. */
export interface CmsSlide {
  id: string;
  imageAttachmentId: string | null;
  title: string | null;
  link: string | null;
  sort: number;
  isActive: boolean;
  /** Display shape — 'rectangle' full-width carousel (default), 'square' tile row. */
  shape: SlideShape;
}
export const CmsSlideInputSchema = z.object({
  imageAttachmentId: z.string().uuid().nullable().optional(),
  title: z.string().max(191).nullable().optional(),
  link: z.string().max(500).nullable().optional(),
  sort: z.number().int().optional(),
  isActive: z.boolean().optional(),
  shape: z.enum(SLIDE_SHAPES).optional(),
});
export type CmsSlideInput = z.infer<typeof CmsSlideInputSchema>;

/** A published content page rendered on the public site (body already sanitized). */
export interface StorefrontPage {
  slug: string;
  title: string;
  bodyHtml: string;
  seoTitle: string | null;
  seoDescription: string | null;
  isHome: boolean;
}

// ---- Store products (staff publishing overlay) ----

/** An item + its store overlay, as shown on the staff "Products" page. */
export interface StoreProduct {
  itemId: string;
  code: string;
  name: string;
  barcode: string | null;
  unitOfMeasure: string | null;
  categoryId: string | null;
  categoryName: string | null;
  brandId: string | null;
  brandName: string | null;
  /** Inactive or hidden items stay out of the store even when published. */
  isActive: boolean;
  isHidden: boolean;
  /** On-hand across warehouses; null when the item isn't inventory-tracked. */
  stock: number | null;
  createdAt: string;
  salePrice: string | null;        // item base price
  saleCurrencyCode: string | null;
  isPublished: boolean;
  storePrice: string | null;       // override; null ⇒ use salePrice
  featured: boolean;
  /** Storefront product-page views (store_products.view_count). */
  views: number;
  sort: number;
  slug: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
}

export const StoreProductUpdateSchema = z.object({
  isPublished: z.boolean().optional(),
  storePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  featured: z.boolean().optional(),
  sort: z.number().int().min(0).max(100000).optional(),
  slug: z.string().min(1).max(160).regex(SLUG).nullable().optional(),
  seoTitle: z.string().max(200).nullable().optional(),
  seoDescription: z.string().max(320).nullable().optional(),
});
export type StoreProductUpdate = z.infer<typeof StoreProductUpdateSchema>;

// ---- Store categories (staff publishing overlay) ----

/** A category + its store overlay, as shown on the staff "Store categories" page. */
export interface StoreCategory {
  categoryId: string;
  code: string;
  name: string;
  parentId: string | null;
  isPublished: boolean;
  slug: string | null;
  descriptionHtml: string | null; // store override; null ⇒ use categories.description
  seoTitle: string | null;
  seoDescription: string | null;
  featured: boolean;
  sort: number;
}

export const StoreCategoryUpdateSchema = z.object({
  isPublished: z.boolean().optional(),
  slug: z.string().min(1).max(160).regex(SLUG).nullable().optional(),
  descriptionHtml: z.string().max(200_000).nullable().optional(),
  seoTitle: z.string().max(200).nullable().optional(),
  seoDescription: z.string().max(320).nullable().optional(),
  featured: z.boolean().optional(),
  sort: z.number().int().min(0).max(100000).optional(),
});
export type StoreCategoryUpdate = z.infer<typeof StoreCategoryUpdateSchema>;

// ---- Public catalog DTOs ----

export interface StorefrontCategory {
  id: string;
  slug: string | null;
  name: string;
}

/** A category as shown in the storefront tree / category cards. */
export interface StorefrontCategoryNode {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  parentId: string | null;
  productCount: number;
  featured: boolean;
}

/** A product brand as shown on the storefront (only brands with published products). */
export interface StorefrontBrand {
  id: string;
  name: string;
  imageUrl: string | null;   // brand logo
  productCount: number;
}

/** A category landing page: the category + breadcrumb ancestors + child categories. */
export interface StorefrontCategoryDetail {
  id: string;
  slug: string;
  name: string;
  descriptionHtml: string;   // sanitized (store override ?? base category description)
  imageUrl: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  breadcrumb: Array<{ slug: string; name: string }>;
  children: StorefrontCategoryNode[];
}

/**
 * "Was / now" for an automatic discount (the `discounts` module), carried by every
 * priced storefront row: the product card, the product page, each packaging unit
 * and each variant.
 *
 * `price` is ALWAYS the charged (already discounted) number, so a client that
 * knows nothing about discounts keeps showing and charging the right amount;
 * `originalPrice` is the strike-through price and exists only to render the
 * comparison. Both fields are null when no rule matched, and ABSENT when the
 * module is off or the server predates it — hence optional, like every other
 * additive storefront DTO field (see `cashbackRedeemed`, `pricing`).
 */
export interface StorefrontDiscountedPrice {
  /** Pre-discount unit price; null ⇒ no discount (nothing to strike through). */
  originalPrice?: string | null;
  /** Whole percent off, for a "-15%" badge; null ⇒ no discount. */
  discountPercent?: number | null;
}

export interface StorefrontProductSummary extends StorefrontDiscountedPrice {
  id: string;
  slug: string | null;    // SEO URL; falls back to id when null
  name: string;
  description: string | null; // short plain-text excerpt for the card subtitle
  price: string;          // resolved: storePrice ?? salePrice (or "0"), less any automatic discount
  currencyCode: string;
  unit?: string;          // base unit of measure (e.g. قطعة, باكيت) — shown beside the price
  imageUrl: string | null;
  inStock: boolean;
  lowStock: boolean;      // tracked, in stock, but at/under the low-stock threshold
  featured: boolean;
  isNew: boolean;         // added to the catalog within the new-arrival window
}

export interface StorefrontProductUnit extends StorefrontDiscountedPrice {
  id: string;
  name: string;
  factor: string;
  /** ABSOLUTE price of one whole pack/carton (never base × factor), less any automatic discount. */
  price: string;
  /** The unit's own scannable barcode (pack/carton), or null. */
  barcode: string | null;
}

/**
 * One (variant × packaging unit) combination, priced by the server.
 *
 * WHY this exists rather than letting the client multiply: checkout prices such a
 * line as (the variant's RAW price × the pack factor) and then applies any
 * automatic discount ONCE, on the wholesale axis. A client multiplying the
 * variant's already-discounted per-piece price would take a fixed amount off
 * factor-many times, and could not know that a retail-only rule does not apply to
 * a pack at all — it never sees the rule. So the server states the answer.
 */
export interface StorefrontVariantUnitPrice extends StorefrontDiscountedPrice {
  unitId: string;
  /** What checkout will charge for one whole pack of this variant. */
  price: string;
}

/** A selectable product variant/option (e.g. "Red / A4") shown on the product page. */
export interface StorefrontProductVariant extends StorefrontDiscountedPrice {
  id: string;
  name: string;
  price: string;           // resolved: variant salePrice ?? base price, less any automatic discount
  barcode: string | null;
  inStock: boolean;
  stockQty: string | null; // null ⇒ not tracked
  /**
   * Price per packaging unit for THIS variant, one entry per unit on the product.
   * Absent/empty when the product has no packaging units.
   */
  unitPrices?: StorefrontVariantUnitPrice[];
}

export interface StorefrontProductDetail extends StorefrontDiscountedPrice {
  id: string;
  slug: string | null;
  /**
   * The shop's own item code, shown on the product page so a shopper can quote
   * it on the phone or in a message. Note it is NOT a unique key and must not be
   * used as one — codes repeat across items on real catalogues, which is why a
   * scan resolves by BARCODE. This is for reading, not for lookup.
   */
  code: string;
  name: string;
  descriptionHtml: string;   // sanitized
  price: string;
  currencyCode: string;
  unitOfMeasure: string;
  images: string[];          // public media URLs
  units: StorefrontProductUnit[];
  variants: StorefrontProductVariant[];
  inStock: boolean;
  categoryName: string | null;
  /** Set only when the item's category is published to the store (linkable). */
  categorySlug: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  /**
   * YouTube video id from the item's video link, already parsed and validated —
   * embed it as `youtube-nocookie.com/embed/<id>`, poster at
   * `img.youtube.com/vi/<id>/hqdefault.jpg`. Null when the item has no video or
   * the stored link is not a YouTube one.
   */
  videoId: string | null;
  /**
   * The shop's own custom fields for this product, already filtered to the ones
   * marked visible to customers and to those with a value — so a client can
   * render the list as-is without knowing what a field definition is.
   */
  customFields: StorefrontCustomField[];
  relatedProducts: StorefrontProductSummary[];
}

/** One custom field as the shopper sees it: a label and a value, nothing else. */
export interface StorefrontCustomField {
  code: string;
  label: string;
  /** Already rendered to text — booleans as yes/no are the client's business. */
  value: string;
}

/**
 * The pricing basis this catalogue response was rendered for. `currencyCode` is
 * AUTHORITATIVE for the shopper's cart: it is the logged-in customer's group
 * currency (e.g. a قرطاسية group priced in USD) or the store currency for guests.
 * Clients must key cart/checkout off THIS, not `StorefrontInfo.currencyCode`,
 * or they will prune the very lines the server just priced.
 */
export interface StorefrontPricingBasis {
  priceListId: string | null;
  priceListName: string | null;
  currencyCode: string;
}

export interface StorefrontCatalog {
  items: StorefrontProductSummary[];
  total: number;
  categories: StorefrontCategory[];
  /** Price range of the matching set (before the price filter) — drives the shop's range slider. */
  priceBounds?: { min: number; max: number };
  /** Effective group + currency for this response (guests ⇒ store currency, null list). */
  pricing?: StorefrontPricingBasis;
}

// ---- Guest checkout ----

export const CheckoutLineSchema = z.object({
  itemId: z.string().uuid(),
  itemUnitId: z.string().uuid().optional(),
  itemVariantId: z.string().uuid().optional(),
  quantity: z.number().int().positive().max(100000),
});

export const CheckoutContactSchema = z.object({
  name: z.string().min(1).max(200),
  // Phone is the customer identity + the guest order-lookup credential. Email
  // was removed from every customer flow; zod strips unknown keys, so an old
  // client that still sends one doesn't hard-break.
  phone: z.string().trim().min(6).max(32),
});

export const CheckoutInputSchema = z.object({
  lines: z.array(CheckoutLineSchema).min(1).max(100),
  contact: CheckoutContactSchema,
  shippingAddress: AddressSchema.optional(),
  // Selected delivery city (name). If it matches an admin-configured city with a
  // fee, the server adds that fee as a delivery line — never trusts a client amount.
  deliveryCity: z.string().trim().max(80).optional(),
  // Optional delivery map pin — persisted onto the customer's party so the
  // delivery module hands the driver the exact drop point.
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  paymentMethod: CheckoutPaymentMethodSchema,
  couponCode: z.string().max(64).optional(),
  note: z.string().max(1000).optional(),
});
export type CheckoutInput = z.infer<typeof CheckoutInputSchema>;

/**
 * Body for the public coupon-preview endpoint (POST /storefront/:slug/coupon/validate
 * and /portal/coupon/validate). Reprices the cart server-side and returns the
 * discount (or a friendly reason it doesn't apply) BEFORE the order is placed, so
 * the shopper never discovers a bad code only when the whole order fails.
 */
export const PublicCouponValidateSchema = z.object({
  code: z.string().trim().min(1).max(64),
  lines: z.array(CheckoutLineSchema).min(1).max(100),
});
export type PublicCouponValidateInput = z.infer<typeof PublicCouponValidateSchema>;

/**
 * Authenticated (portal) checkout: same as guest checkout but WITHOUT a `contact`
 * block — the order is bound to the logged-in customer's own party (and their price
 * list) server-side. Used by POST /api/v1/portal/checkout.
 */
export const PortalCheckoutInputSchema = z.object({
  lines: z.array(CheckoutLineSchema).min(1).max(100),
  shippingAddress: AddressSchema.optional(),
  deliveryCity: z.string().trim().max(80).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  paymentMethod: CheckoutPaymentMethodSchema,
  couponCode: z.string().max(64).optional(),
  note: z.string().max(1000).optional(),
  /**
   * How much of the customer's cashback balance to APPLY to this order (base
   * currency). Server-authoritative: capped at the real wallet balance and the
   * order total; refused when the order isn't in the base currency. Absent/0 ⇒
   * no redemption. Only the authenticated portal checkout accepts it — guests
   * have no wallet.
   */
  cashbackAmount: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
});
export type PortalCheckoutInput = z.infer<typeof PortalCheckoutInputSchema>;

/** Body for POST /portal/checkout/quote — the cart to price a cashback quote for. */
export const PortalCheckoutQuoteInputSchema = z.object({
  lines: z.array(CheckoutLineSchema).min(1).max(100),
});
export type PortalCheckoutQuoteInput = z.infer<typeof PortalCheckoutQuoteInputSchema>;

/**
 * Server-computed cashback quote for the checkout page. `eligible` is false when
 * the cashback module/online channel is off or the buyer's order currency isn't
 * the base (wallet) currency — the client then hides the whole widget. `willEarn`
 * is the estimated earn (eligible items only, rate applied server-side) credited
 * when the order is FULFILLED.
 */
export interface PortalCheckoutQuote {
  eligible: boolean;
  /** Wallet currency (tenant base). */
  currencyCode: string;
  /** The customer's current wallet balance (base currency). */
  balance: string;
  /** Estimated earn for this cart when fulfilled ('0' when nothing qualifies). */
  willEarn: string;
  /** Max share of the order payable with cashback (0–100). */
  maxRedeemPct: number;
}

export interface CheckoutResult {
  orderNumber: string;
  /** The created order's id (UUID) — lets a signed-in client open the order detail directly. */
  id?: string;
  /** Cashback actually applied to the order (base currency), when requested. */
  cashbackUsed?: string;
}

export interface StorefrontOrderLine {
  description: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
  /** Source item + chosen unit — present on portal orders so the app can re-add them to the cart (Reorder). */
  itemId?: string | null;
  itemUnitId?: string | null;
}

/** Live delivery tracking for a customer's order (when the delivery module created one). */
export interface StorefrontOrderDelivery {
  /** pending | assigned | out_for_delivery | delivered | failed */
  status: string;
  driverName: string | null;
  assignedAt: string | null;
  deliveredAt: string | null;
}

/** A guest's order, returned by the public lookup (number + phone). */
export interface StorefrontOrder {
  orderNumber: string;
  date: string;
  status: string;
  currencyCode: string;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  total: string;
  /** Cashback applied at checkout (base currency; '0' when none). Optional so older APIs stay compatible. */
  cashbackRedeemed?: string;
  /** Checkout method key ('fib' | 'cod' | …); optional so older APIs stay compatible. */
  paymentMethod?: string | null;
  /**
   * True once a FIB payment for this order is CONFIRMED by FIB's own API —
   * the server's fact, never the client's claim. Drives "paid" chips and
   * hides the pay button so an order can't be paid twice.
   */
  fibPaid?: boolean;
  lines: StorefrontOrderLine[];
  /** Present (authenticated portal order detail) when a delivery exists; null/absent otherwise. */
  delivery?: StorefrontOrderDelivery | null;
}

// ---- Staff online-order management ----

/** Real sales_orders statuses an online order can be in. */
export const OnlineOrderStatusSchema = z.enum(['submitted', 'fulfilled', 'cancelled']);
export type OnlineOrderStatus = z.infer<typeof OnlineOrderStatusSchema>;

export interface OnlineOrderSummary {
  id: string;
  orderNumber: string;
  date: string;
  status: OnlineOrderStatus;
  customerName: string;
  paymentMethod: string | null;
  /** True once FIB confirmed the online payment — staff must not collect again. */
  fibPaid?: boolean;
  currencyCode: string;
  total: string;
}

export interface OnlineOrderDetail extends OnlineOrderSummary {
  customerEmail: string | null;
  customerPhone: string | null;
  shippingAddress: Address | null;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  /** Cashback the customer applied at checkout (base currency; '0' when none). */
  cashbackRedeemed: string;
  notes: string | null;
  lines: StorefrontOrderLine[];
  /** The invoice the order became («منجز» bills it); null while it is still an order. */
  invoice?: { id: string; docNumber: string; status: string } | null;
}

/** Staff may move a submitted order to fulfilled or cancelled (terminal states). */
export const OnlineOrderStatusUpdateSchema = z.object({
  status: z.enum(['fulfilled', 'cancelled']),
});
export type OnlineOrderStatusUpdate = z.infer<typeof OnlineOrderStatusUpdateSchema>;
